import { memo, useMemo } from 'react';

import { alpha, Box, Stack, Typography, useTheme } from '@mui/material';

import type { ComparisonMapItem } from './ComparisonMap';
import type { PriorityGroup } from '@/features/offlineSalesOptimizer/types';

import { priorityGroupsNames } from '@/features/offlineSalesOptimizer/common';

const PRIORITY_GROUP_ORDER: PriorityGroup[] = ['L', 'M', 'H', 'VH'];

interface ComparisonMapLegendProps {
  data: ComparisonMapItem[] | undefined;
  colors: string[];
}

const ComparisonMapLegend = ({ data, colors }: ComparisonMapLegendProps) => {
  const theme = useTheme();

  const { bucketCounts, naCount } = useMemo(() => {
    const counts: Record<PriorityGroup, number> = { L: 0, M: 0, H: 0, VH: 0 };
    let nullCount = 0;
    data?.forEach(({ bucket }) => {
      if (bucket) {
        counts[bucket]++;
      } else {
        nullCount++;
      }
    });
    return { bucketCounts: counts, naCount: nullCount };
  }, [data]);

  if (!data?.length) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        bgcolor: alpha(theme.palette.background.paper, 0.7),
        backdropFilter: 'blur(8px)',
        boxShadow: theme.customShadows.xs,
        bottom: theme.spacing(5),
        right: theme.spacing(1.5),
        borderRadius: 0.5,
        p: 1.5,
      }}
    >
      {naCount > 0 && (
        <Stack direction='row' alignItems='center' gap={1}>
          <Box
            sx={{ width: 18, height: 18, bgcolor: 'grey.300', flexShrink: 0 }}
          />
          <Typography variant='subtitleLabel' width={58}>
            N/A
          </Typography>
          <Typography variant='subtitleLabel'>(N = {naCount})</Typography>
        </Stack>
      )}

      {PRIORITY_GROUP_ORDER.map((group, index) => (
        <Stack key={group} direction='row' alignItems='center' gap={1}>
          <Box
            sx={{
              width: 18,
              height: 18,
              bgcolor: colors[index],
              flexShrink: 0,
            }}
          />
          <Typography variant='subtitleLabel' width={58}>
            {priorityGroupsNames[group]}
          </Typography>
          <Typography variant='subtitleLabel'>
            (N = {bucketCounts[group]})
          </Typography>
        </Stack>
      ))}
    </Box>
  );
};

export default memo(ComparisonMapLegend);
